/**
 * <garage-panel> — renders the customer's machines from window.Garage.
 *
 * Three modes, set via the `mode` attribute:
 *   compact — the active machine only (the machine strip)
 *   nav     — the header dropdown: one row per machine, the name a link to
 *             its parts; remove lives in the modal
 *   pick    — the homepage strip: every machine, the current one first, with
 *             Current / Make current and Shop parts; remove lives in the
 *             modal
 *   full    — every saved machine, with switch, shop and remove
 *
 * Renders off `garage:changed`, so it stays in sync with the finder and with
 * other tabs without any coordination between components.
 */
class GaragePanel extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;

    this.mode = this.getAttribute('mode') || 'full';
    // Nav dropdown shows a short list; the modal shows everything.
    this.limit = Number(this.getAttribute('limit')) || Infinity;
    this.list = this.querySelector('[data-garage-list]');
    this.empty = this.querySelector('[data-garage-empty]');

    this.status = this.querySelector('[data-garage-status]');
    this.sync = this.querySelector('[data-garage-sync]');

    this.onChanged = (event) => {
      this.render();
      this.announce(event.detail && event.detail.change);
    };
    document.addEventListener('garage:changed', this.onChanged);
    this.onSync = (event) => this.paintSync(event.detail);
    document.addEventListener('garage:sync', this.onSync);

    // Delegated so buttons rendered later still work.
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-garage-action]');
      if (!button) return;

      const { garageAction: action, garageKey: key } = button.dataset;
      if (action === 'activate') {
        window.Garage.setActive(key);
      } else if (action === 'remove') {
        window.Garage.remove(key);
      } else if (action === 'shop') {
        // In the dropdown this is a link: a modified click (a new tab, say)
        // is the browser's; a plain one makes the machine current on the way.
        if (button.tagName === 'A' && (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0)) return;
        event.preventDefault();
        window.Garage.setActive(key);
        window.location.href = `/collections/${key}`;
      }
    });

    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('garage:changed', this.onChanged);
    document.removeEventListener('garage:sync', this.onSync);
  }

  /** One line about what just happened, cleared after a moment. */
  announce(change) {
    if (!this.status || !change) return;
    const vehicle = window.Garage.vehicles.find((v) => v.key === change.key);
    const name = vehicle ? (vehicle.nickname || vehicle.label) : 'That machine';
    let text = '';
    if (change.type === 'add') {
      text = change.existed
        ? `${name} was already in your garage — it is now your current machine.`
        : `${name} saved to your garage and set as current.`;
    } else if (change.type === 'remove') {
      text = 'Removed from your garage.';
    } else if (change.type === 'activate') {
      text = `${name} is now your current machine.`;
    }
    this.status.textContent = text;
    clearTimeout(this.statusTimer);
    this.statusTimer = setTimeout(() => { if (this.status) this.status.textContent = ''; }, 5000);
  }

  /** Account sync is never silently assumed: a failure is said out loud. */
  paintSync(detail) {
    if (!this.sync) return;
    if (detail && detail.ok) {
      this.sync.hidden = true;
      this.sync.textContent = '';
      return;
    }
    this.sync.textContent = `Saved on this device, but not to your account yet. ${(detail && detail.reason) || ''} It will try again on the next change.`.replace(/\s+/g, ' ').trim();
    this.sync.hidden = false;
  }

  render() {
    if (!this.list || !window.Garage) return;

    const active = window.Garage.active();
    let all = this.mode === 'compact'
      ? (active ? [active] : [])
      : window.Garage.vehicles;
    // The strip leads with the current machine: it is the one most likely wanted.
    if (this.mode === 'pick' && active) {
      all = [active, ...all.filter((v) => v.key !== active.key)];
    }

    // Keep the active machine visible even if it isn't among the newest few.
    let vehicles = all.slice(0, this.limit);
    if (active && !vehicles.some((v) => v.key === active.key)) {
      vehicles = [active, ...all.filter((v) => v.key !== active.key)].slice(0, this.limit);
    }

    this.toggleAttribute('data-empty', all.length === 0);
    if (this.empty) this.empty.hidden = all.length > 0;

    const frag = document.createDocumentFragment();
    for (const vehicle of vehicles) {
      frag.appendChild(this.mode === 'nav' ? this.navRow(vehicle, active) : this.row(vehicle, active));
    }

    // Never truncate silently — someone who saved five machines and sees three
    // would reasonably think two were lost.
    if (all.length > vehicles.length) {
      const more = document.createElement('li');
      more.className = 'garage-panel__more';
      more.textContent = `Showing ${vehicles.length} of ${all.length} machines`;
      frag.appendChild(more);
    }

    this.list.replaceChildren(frag);
  }

  /**
   * The dropdown's row: the whole row is a link to the machine's parts (and
   * makes it current on the way); the active one says "Current" where the
   * others carry a chevron. Four buttons a row made the column taller than
   * the panel, and the panel scrolled (Lucas, 2026-10-10); remove is the
   * modal's.
   */
  navRow(vehicle, active) {
    const isActive = active && active.key === vehicle.key;

    const li = document.createElement('li');
    li.className = 'garage-panel__item';
    if (isActive) li.setAttribute('data-active', '');

    const link = document.createElement('a');
    link.className = 'garage-panel__link';
    link.href = `/collections/${encodeURIComponent(vehicle.key)}`;
    link.dataset.garageAction = 'shop';
    link.dataset.garageKey = vehicle.key;
    if (isActive) link.setAttribute('aria-current', 'true');

    const name = document.createElement('span');
    name.className = 'garage-panel__name';
    // textContent, not innerHTML — nicknames are customer input.
    name.textContent = vehicle.nickname || vehicle.label;
    if (vehicle.nickname) name.title = vehicle.label;
    link.appendChild(name);

    const mark = document.createElement('span');
    if (isActive) {
      mark.className = 'garage-panel__state';
      mark.textContent = 'Current';
    } else {
      mark.className = 'garage-panel__chevron';
      mark.setAttribute('aria-hidden', 'true');
    }
    link.appendChild(mark);

    li.appendChild(link);
    return li;
  }

  row(vehicle, active) {
    const isActive = active && active.key === vehicle.key;

    const li = document.createElement('li');
    li.className = 'garage-panel__item';
    if (isActive) li.setAttribute('data-active', '');

    const name = document.createElement('span');
    name.className = 'garage-panel__name';
    // textContent, not innerHTML — nicknames are customer input.
    name.textContent = vehicle.nickname || vehicle.label;
    if (vehicle.nickname) name.title = vehicle.label;
    li.appendChild(name);

    if (this.mode === 'compact') {
      li.appendChild(this.button('Shop parts', 'shop', vehicle.key));
      return li;
    }

    const actions = document.createElement('span');
    actions.className = 'garage-panel__actions';

    // "Current" takes the same slot, shape and width as "Make current" on the
    // other rows, so the buttons line up in a column down the list.
    if (isActive) {
      const current = document.createElement('span');
      current.className = 'garage-panel__button garage-panel__button--current';
      current.textContent = 'Current';
      actions.appendChild(current);
    } else {
      actions.appendChild(this.button('Make current', 'activate', vehicle.key));
    }

    actions.appendChild(this.button('Shop parts', 'shop', vehicle.key));
    // Remove is management, kept to the modal; the strip picks. (Rename was
    // here too; dropped at Lucas's request, 2026-10-15, request 157 — a saved
    // nickname from before still shows.)
    if (this.mode === 'full') {
      actions.appendChild(
        this.button('Remove', 'remove', vehicle.key, `Remove ${vehicle.label}`)
      );
    }
    li.appendChild(actions);

    return li;
  }

  button(label, action, key, ariaLabel) {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = `garage-panel__button garage-panel__button--${action}`;
    el.textContent = label;
    el.dataset.garageAction = action;
    el.dataset.garageKey = key;
    if (ariaLabel) el.setAttribute('aria-label', ariaLabel);
    return el;
  }
}

if (!customElements.get('garage-panel')) {
  customElements.define('garage-panel', GaragePanel);
}

/**
 * Keeps any [data-garage-count] badge in step with the garage — used by the nav
 * trigger, which shows how many machines are saved without opening the panel.
 * Handled here rather than inside <garage-panel> because the badge lives on the
 * trigger, outside the panel element.
 */
function paintGarageCounts() {
  const count = window.Garage?.count ?? 0;
  for (const badge of document.querySelectorAll('[data-garage-count]')) {
    badge.textContent = String(count);
    badge.hidden = count === 0;
  }
}

document.addEventListener('garage:changed', paintGarageCounts);
